import { Badge, Flex, Text } from "@chakra-ui/react-v3";
import { SmartObservation } from "@metriport/fhir-sdk";
import { getValueAndInterpretation } from "../../shared-components/fhir-sections/labs/shared";
import { ResourceCardLayout } from "../components/resource-card-layout";
import { getDocIdExtension } from "../utils/document-utils";
import { getDateForResourceType } from "../utils/resource-value-extractors";

export type LabTimelineItemProps = {
  resource: SmartObservation;
  isFirst?: boolean;
};

/**
 * Lab-specific timeline item that displays lab results with value, unit, interpretation, and reference range.
 */
export function LabTimelineItem({ resource, isFirst }: LabTimelineItemProps) {
  const date = getDateForResourceType(resource);
  const docId = getDocIdExtension(resource);

  // Use shared utility for value, unit, interpretation, and reference range
  const { value, unit, interpretation, referenceRange } = getValueAndInterpretation(resource);

  if (!date) return null;

  return (
    <ResourceCardLayout
      date={date}
      isFirst={isFirst}
      docId={docId}
      resourceType="Observation"
      resources={[resource]}
    >
      <Flex gap={2} wrap="wrap" align="center">
        {/* Value and unit */}
        <Text fontSize="md" fontWeight="medium" color="fg.emphasized">
          {value?.toString() || "-"}
          {unit && ` ${unit}`}
        </Text>

        {/* Interpretation badge */}
        {interpretation && (
          <Badge colorPalette={getInterpretationColor(interpretation)} size="sm" variant="subtle">
            {interpretation}
          </Badge>
        )}
      </Flex>

      {/* Reference range if available */}
      {referenceRange && (referenceRange.low || referenceRange.high || referenceRange.text) && (
        <Text fontSize="sm" color="fg.muted" mt={1}>
          Reference: {formatReferenceRange(referenceRange)}
        </Text>
      )}
    </ResourceCardLayout>
  );
}

function formatReferenceRange(range: {
  low?: number;
  high?: number;
  unit?: string;
  text?: string;
}): string {
  const { low, high, unit, text } = range;

  if (low !== undefined && high !== undefined) {
    return `${low} - ${high}${unit ? ` ${unit}` : ""}`;
  }
  if (low !== undefined) {
    return `≥ ${low}${unit ? ` ${unit}` : ""}`;
  }
  if (high !== undefined) {
    return `≤ ${high}${unit ? ` ${unit}` : ""}`;
  }

  return text || "-";
}

function getInterpretationColor(interpretation: string): string {
  const lower = interpretation.toLowerCase();
  if (lower.includes("high") || lower.includes("abnormal")) return "red";
  if (lower.includes("low")) return "orange";
  if (lower.includes("normal")) return "green";
  return "gray";
}
